import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Palette } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { DESIGN_STAGES } from "@/lib/design";
import ScopeToggle, { readScope } from "@/components/common/ScopeToggle";
import PendingQueueBanner from "@/components/PendingQueueBanner";
import ProjectSearchBar, { ProjectSearchEmpty, projectMatches } from "@/components/shared/ProjectSearchBar";
import TeamSummaryChips, { useProjectTeams } from "@/components/shared/TeamSummaryChips";

export default function DesignProjectsPage() {
  const { roleName } = useAuth();
  const isDH = roleName === "Design Head";
  const isDM = roleName === "Design Manager";
  const showScope = isDH || isDM;
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState(readScope("design.projects.scope", "mine"));
  // Feb 2026 · CRM Executive city scoping
  const [cityFilter, setCityFilter] = useState("all");
  const [myCities, setMyCities] = useState([]);
  const [unrestricted, setUnrestricted] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/me/assigned-cities")
      .then(({ data }) => {
        setMyCities(data.assigned_cities || []);
        setUnrestricted(!!data.unrestricted);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = showScope ? `/design/projects?scope=${scope}` : "/design/projects";
      const { data } = await api.get(url);
      setProjects(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [showScope, scope]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let base = projects;
    if (cityFilter !== "all") {
      const cf = cityFilter.toLowerCase();
      base = base.filter((p) => (p.city || "").toLowerCase() === cf);
    }
    return base.filter((p) => projectMatches(p, q));
  }, [q, projects, cityFilter]);

  // Feb 24, 2026 · batch-fetch team assignments for every visible project
  // so the row chips render in a single round-trip.
  const teamsMap = useProjectTeams(filtered.map(p => p.id));

  const stats = {
    total: projects.length,
    active: projects.filter(p => !p.production_ready).length,
    productionReady: projects.filter(p => p.production_ready).length,
    totalIterations: projects.reduce((n, p) => n + (p.iteration_count || 0), 0),
  };

  const title = roleName === "Interior Designer" ? "My Projects"
    : roleName === "Design Manager" ? "Team Projects"
    : "All Design Projects";

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Design · Workspace</p>
            <h1 className="font-display text-3xl text-white mt-1">{title}</h1>
          </div>
          {showScope && (
            <ScopeToggle
              value={scope}
              onChange={setScope}
              storageKey="design.projects.scope"
              testIdPrefix="design-projects-scope"
            />
          )}
        </div>
      </div>

      <PendingQueueBanner testid="pending-queue-designer"/>

      <div className="px-10 py-8">
        {/* KPI Matrix */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <KpiCard label="Active Projects" value={stats.active} gold/>
          <KpiCard label="Total Projects" value={stats.total}/>
          <KpiCard label="Variations Shared" value={stats.totalIterations} gold/>
          <KpiCard label="Production Ready" value={stats.productionReady}/>
        </div>

        <ProjectSearchBar
          value={q}
          onChange={setQ}
          resultCount={filtered.length}
          totalCount={projects.length}
          testid="design-projects-search"
        />
        {!unrestricted && myCities.length > 0 && (
          <div className="flex items-center gap-2 mb-4" data-testid="crm-city-filter-container">
            <label className="text-[10px] font-mono uppercase tracking-widest text-gray-500 shrink-0">
              City ({myCities.length})
            </label>
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="iz-input text-sm"
              data-testid="crm-city-filter"
            >
              <option value="all">All Assigned Cities</option>
              {myCities.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}

        <div className="iz-card overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#262626] bg-[#0f0f0f]">
                {["Project","Client","City","Stage","Iter.","45% Paid","Prod.","Execution"].map(h => (
                  <th key={h} className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-500 py-4 px-5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-10 text-center text-gray-500">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-0">
                    {q.trim() ? (
                      <ProjectSearchEmpty query={q} testid="design-projects-search-empty" />
                    ) : (
                      <p className="p-10 text-center text-gray-500">No projects.</p>
                    )}
                  </td>
                </tr>
              ) : filtered.map(p => {
                const stage = DESIGN_STAGES.find(s => s.id === p.status);
                return (
                  <tr key={p.id} className="border-b border-[#1a1a1a] hover:bg-[#0e0e0e] cursor-pointer" onClick={() => nav(`/design/projects/${p.id}`)} data-testid={`project-row-${p.id}`}>
                    <td className="py-4 px-5">
                      <p className="text-sm text-white">{p.project_name}</p>
                      <p className="text-[10px] font-mono text-gray-500">₹{(p.project_amount || 0).toLocaleString()}</p>
                      <div className="mt-2">
                        <TeamSummaryChips projectId={p.id} slots={teamsMap[p.id]} />
                      </div>
                    </td>
                    <td className="py-4 px-5 text-sm text-gray-300">{p.client_name}</td>
                    <td className="py-4 px-5 text-xs text-gray-400">{p.city || "—"}</td>
                    <td className="py-4 px-5">
                      <span className="inline-flex items-center gap-2 text-xs">
                        <Palette className="w-3 h-3 text-[#d4af37]"/>
                        <span className="text-gray-300">{stage?.short || p.status}</span>
                      </span>
                    </td>
                    <td className="py-4 px-5 text-sm text-[#d4af37] font-mono">{p.iteration_count || 0}</td>
                    <td className="py-4 px-5">
                      <span className={`text-[10px] font-mono uppercase tracking-widest ${p.is_45_percent_paid ? "text-[#10b981]" : "text-gray-500"}`}>
                        {p.is_45_percent_paid ? "Paid" : "Pending"}
                      </span>
                    </td>
                    <td className="py-4 px-5">
                      <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded ${p.production_ready ? "text-[#d4af37] bg-[#d4af37]/10" : "text-gray-500 bg-[#1a1a1a]"}`}>
                        {p.production_ready ? "Ready" : "—"}
                      </span>
                    </td>
                    <td className="py-4 px-5" data-testid={`exec-cell-${p.id}`}>
                      {p.execution_phase_active ? (
                        <span className="inline-flex flex-col text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border border-[#d4af37]/40 text-[#d4af37] bg-[#d4af37]/5"
                              data-testid={`exec-badge-${p.id}`}>
                          <span>In Execution</span>
                          {p.electrical_meeting_summary?.meeting_date && (
                            <span className="text-[9px] text-gray-400 normal-case tracking-normal mt-0.5">
                              ⚡ {p.electrical_meeting_summary.meeting_date}
                              {p.electrical_meeting_summary.meeting_time ? ` · ${p.electrical_meeting_summary.meeting_time}` : ""}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-gray-600">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, gold }) {
  return (
    <div className={`iz-card p-5 ${gold ? "border-[#d4af37]/40" : ""}`}>
      <p className={`text-[10px] font-mono uppercase tracking-[0.25em] ${gold ? "text-[#d4af37]" : "text-gray-500"}`}>{label}</p>
      <p className={`font-display text-3xl mt-2 ${gold ? "text-[#d4af37]" : "text-white"}`}>{value}</p>
    </div>
  );
}
